import {
    type CardBullet,
    CLICKED_MEDICARE_BANNER_CHOICE_KEY,
    type PathwayAlertConfig,
    type PathwayOptionConfig,
    type PathwayQuestionnaireConfig,
    type QuestionnaireOption,
} from '../shared';
import {
    NO_INSURANCE_BULLET,
    NO_PRIOR_AUTH_BULLET,
    READY_FAST_BULLET,
    STANDARD_COMMERCIAL_QUESTIONNAIRE_OPTION,
} from '../shared-content';

// ─── Constants ────────────────────────────────────────────────────────────────

export const ZEPBOUND_TERMS_URL =
    'https://www.lilly.com/lillydirect/medicines/zepbound/zepbound-tirzepatide-full-terms-conditions#selfpaysavings';

export const ZEPBOUND_JOURNEY_TERMS_URL =
    'https://www.lilly.com/lillydirect/medicines/zepbound/zepbound-tirzepatide-full-terms-conditions#journey';

export const REVIEW_DETAILS_DESCRIPTION =
    'Review the details of each option before you continue.';

// ─── Zepbound-specific bullets ────────────────────────────────────────────────

export const PHARMACY_COVERAGE_BULLET: CardBullet = {
    key: 'pharmacyCoverage',
    icon: 'check',
    color: 'success',
    text: 'Coverage checked by your pharmacy',
};

// Bridge-specific PA bullet
export const BRIDGE_PRIOR_AUTH_BULLET: CardBullet = {
    key: 'priorAuth',
    icon: 'warning',
    color: 'warning',
    text: '<b>Prior authorization (PA)</b><br />Medicare GLP\u20111 Bridge requires insurance approval with support from your provider, which may take time.',
};

// ─── Base cards ───────────────────────────────────────────────────────────────

// Base Medicare GLP\u20111 Bridge card — same across all Zepbound products.
// KwikPen adds content.priceLabel. Vial overrides content.priceNote with a linked version.
export const ZEPBOUND_MEDICARE_BRIDGE_CARD_BASE: PathwayOptionConfig = {
    id: 'medicare-bridge',
    fillPathway: 'government',
    recommended: true,
    destination: 'pharmacy-select',
    headlinePriceType: 'MedicareBridge',
    content: {
        heading: 'Medicare GLP\u20111 Bridge',
        headerIcon: 'security',
        subHeading: 'with the Zepbound® KwikPen®',
        showPrice: true,
        priceSuperscript: '*',
        priceNote:
            '<caption>*Eligibility based on <a href="https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge">Medicare GLP\u20111 Bridge Clinical Criteria</a>. Certain plan types excluded. Not a guarantee of coverage.</caption>',
        fallbackPriceLabel: '<b>If not approved</b>, self-pay starting at',
        fallbackSuperscript: '‡',
        bullets: [PHARMACY_COVERAGE_BULLET, BRIDGE_PRIOR_AUTH_BULLET],
        button: 'Use Medicare GLP\u20111 Bridge',
    },
};

// Base self-pay card — shared between KwikPen and AutoInjector.
// KwikPen adds destination: 'pharmacy-select'.
export const ZEPBOUND_SELF_PAY_CARD_BASE: PathwayOptionConfig = {
    id: 'self-pay',
    fillPathway: 'self-pay',
    headlinePriceType: 'CashAssistance',
    content: {
        heading: 'Self-Pay',
        headerIcon: 'account-balance-wallet',
        showPrice: true,
        priceLabel: 'Starting at',
        priceSuperscript: '‡',
        bullets: [NO_INSURANCE_BULLET, NO_PRIOR_AUTH_BULLET, READY_FAST_BULLET],
        button: 'Use self-pay',
    },
};

// ─── Alert content ────────────────────────────────────────────────────────────

export const MEDICARE_BRIDGE_ALERT_CONTENT =
    '<b>Eligible Medicare Part D patients may have coverage through the Medicare GLP\u20111 Bridge.</b>';

export const MEDICARE_BRIDGE_ALERT_CTA: NonNullable<PathwayAlertConfig['cta']> =
    {
        text: 'I have Medicare Part D',
        patientChoiceKey: CLICKED_MEDICARE_BANNER_CHOICE_KEY,
        target: {
            pathway: 'government',
            subPathway: 'medicare-bridge',
            destination: 'pharmacy-select',
        },
    };

// ─── Questionnaire ────────────────────────────────────────────────────────────

export const STANDARD_GOVERNMENT_SUB_QUESTIONNAIRE: PathwayQuestionnaireConfig =
    {
        heading: 'Do you have Medicare Part D?',
        description:
            "Coverage options vary by plan type — we'll use your answer to find the right path forward for you.",
        options: [
            {
                id: 'medicare',
                content: {
                    heading: 'Yes — I have Medicare Part D.',
                    description:
                        'Traditional Medicare with Part D, or a Medicare Advantage plan with prescription drug coverage.',
                },
                target: {
                    pathway: 'government',
                    subPathway: 'medicare-bridge',
                },
            },
            {
                id: 'other-government',
                content: {
                    heading:
                        'No — I have another type of government insurance.',
                },
                target: {
                    pathway: 'government',
                    subPathway: 'other-government',
                },
            },
        ],
    };

// First two options are identical across Zepbound drugs with a full insurance questionnaire.
// Spread these into the options array and add drug-specific options after.
export const STANDARD_INSURANCE_QUESTIONNAIRE_OPTIONS: QuestionnaireOption[] = [
    STANDARD_COMMERCIAL_QUESTIONNAIRE_OPTION,
    {
        id: 'government',
        content: {
            heading: 'I have government insurance',
            description:
                'Such as Medicare, Medicaid, TRICARE, Veterans Affairs, Indian Health Services, or Bureau of Prisons',
        },
        subQuestionnaire: STANDARD_GOVERNMENT_SUB_QUESTIONNAIRE,
    },
];
